import React, { useState, useEffect } from 'react'
import { TaskService } from '@duck/api-definitions'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@duck/ui'
import { TaskForm } from './TaskForm'
import { apis } from '@modules/apiClients'
import { useTaskValidation } from '../../hooks/useTaskValidation'
import { AxiosError } from 'axios'
import { useTar } from '@/modules'

interface TaskDialogProps {
  isOpen: boolean
  onClose: () => void
  task: TaskService.Task | null
  onTaskSaved?: () => void
  allAccessGroups: string[]
}


export const TaskDialog: React.FC<TaskDialogProps> = ({ isOpen, onClose, task, onTaskSaved, allAccessGroups }) => {
  const [formData, setFormData] = useState<Partial<TaskService.TaskBase>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const { validateTask, clearErrors, fieldErrors } = useTaskValidation()
  const { t } = useTar()

  // Reset form data when task changes
  useEffect(() => {
    if (task) {
      const { id, ...taskData } = task
      setFormData({ ...taskData })
    } else {
      setFormData({})
    }
    setSaveError(null)
    setIsSaving(false)
    clearErrors()
  }, [task, clearErrors])

  const handleInputChange = (field: keyof TaskService.Task, value: string | number | string[] | undefined) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      setSaveError(null)

      // Validate the form data using ZOD schemas
      const isNewTask = !task
      const validation = validateTask(formData, isNewTask)

      if (!validation.isValid) {
        const errorMessages = Object.entries(validation.errors)
          .map(([field, msg]) => `${field}: ${msg}`)
          .join('; ')
        setSaveError(`Validation failed: ${errorMessages}`)
        return
      }

      if (task) {
        // Update existing task
        await apis.taskService.idPut(task.id, formData as TaskService.TaskBase)
      } else {
        // Create new task
        await apis.taskService.rootPost(formData as TaskService.TaskBase)
      }

      // Call the callback to refresh parent component
      onTaskSaved?.()
      onClose()
    } catch (error) {
      console.error('Failed to save task:', error)
      setSaveError(error instanceof AxiosError ? error?.response?.data?.error : 'Failed to save task. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!task) return

    try {
      setIsSaving(true)
      setSaveError(null)

      // Delete the task
      await apis.taskService.idDelete(task.id)

      // Call the callback to refresh parent component
      onTaskSaved?.()
      onClose()
    } catch (error) {
      console.error('Failed to delete task:', error)
      setSaveError(error instanceof AxiosError ? error?.response?.data?.error : 'Failed to delete task. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }


  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !isSaving && !open && onClose()}
    >
      <DialogContent
        className="sm:max-w-5xl lg:max-w-6xl xl:max-w-7xl sm:min-w-[600px] lg:min-w-[800px] max-h-[90vh] "
        onPointerDownOutside={(e) => isSaving && e.preventDefault()}
        onEscapeKeyDown={(e) => isSaving && e.preventDefault()}
        onInteractOutside={(e) => isSaving && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {task ? `${t('tasks.editTask')} ${task.name}` : t('tasks.newTask')}
          </DialogTitle>
        </DialogHeader>

        <TaskForm
          formData={formData}
          onChange={handleInputChange}
          onSave={handleSave}
          onCancel={onClose}
          onDelete={task ? handleDelete : undefined}
          isSaving={isSaving}
          saveError={saveError}
          fieldErrors={fieldErrors}
          allAccessGroups={allAccessGroups}
        />
      </DialogContent>
    </Dialog>
  )
}
